import React from "react";
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@material-ui/core";
import exerciseConstants from "constants/Exercises/exerciseConstants";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles(() => ({
  formControl: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#ffffff !important",
      "& fieldset": {
        borderColor: "#4a5568 !important",
        borderWidth: "1.5px !important",
      },
      "&:hover fieldset": {
        borderColor: "#3f51b5 !important",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#3f51b5 !important",
        borderWidth: "2px !important",
      },
    },
    "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
      backgroundColor: "#ffffff !important",
      padding: "0 8px !important",
      color: "#3f51b5 !important",
      fontWeight: "700 !important",
      zIndex: 2,
    },
    "& .MuiOutlinedInput-input": {
      color: "#1a202c !important",
    },
    "& .MuiSelect-icon": {
      color: "#1a202c !important",
    },
  },
}));

/**
 * Componente de cabeçalho para filtro de exercícios.
 *
 * Renderiza um seletor (dropdown) estilizado para filtrar exercícios
 * por tipo, adaptando dinamicamente as opções conforme o tipo de exercício
 * (Máquina ou Gramática).
 *
 * A estilização segue o padrão visual definido no formulário principal,
 * utilizando sobrescrita de classes internas do :contentReference[oaicite:0]{index=0}
 * para garantir consistência visual (cores, bordas e comportamento do label).
 *
 * O componente não é renderizado quando o tipo é "REGULAR_EXPRESSIONS".
 *
 * @author Willian Brandao
 *
 * @param {Object} props Propriedades do componente.
 * @param {string} props.type Tipo de exercício ('MACHINE', 'GRAMMAR' ou 'REGULAR_EXPRESSIONS').
 * @param {string} props.language Idioma da interface ('pt' ou 'en').
 * @param {string} props.selectedType Valor atualmente selecionado no filtro.
 * @param {Function} props.setSelectedType Função para atualizar o tipo selecionado.
 *
 * @returns {JSX.Element|null} Retorna o componente de filtro ou null se não aplicável.
 *
 * @example
 * <ExerciseFilterHeader
 *   type="MACHINE"
 *   language="pt"
 *   selectedType="AF"
 *   setSelectedType={setSelectedType}
 * />
 */

export default function ExerciseFilterHeader({
  type,
  language,
  selectedType,
  setSelectedType,
}) {
  if (type === "REGULAR_EXPRESSIONS") {
    return null;
  }

  const classes = useStyles();
  const filterConstants = exerciseConstants.list.filter;
  const labelText = filterConstants.labelText[language];

  return (
    <Box mb={2}>
      <FormControl
        variant="outlined"
        fullWidth
        size="small"
        className={classes.formControl}
      >
        <InputLabel id="type-filter-label" shrink>
          {labelText}
        </InputLabel>

        <Select
          labelId="type-filter-label"
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          label={labelText}
        >
          <MenuItem value="all">{filterConstants.all[language]}</MenuItem>

          {type === "MACHINE" &&
            Object.keys(exerciseConstants.machineTypes).map((key) => {
              const labelObj = exerciseConstants.machineTypes[key];
              return (
                <MenuItem key={key} value={key}>
                  {labelObj[language]}
                </MenuItem>
              );
            })}

          {type === "GRAMMAR" &&
            Object.keys(exerciseConstants.grammarTypes).map((key) => {
              const labelObj = exerciseConstants.grammarTypes[key];
              return (
                <MenuItem key={key} value={key}>
                  {labelObj[language]}
                </MenuItem>
              );
            })}
        </Select>
      </FormControl>
    </Box>
  );
}
